import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { Leaf } from 'lucide-react';
import { API } from '../App';
import { devError } from '../utils/logger';

/**
 * Anonymised, country-scale heatmap of available stock locations.
 *
 * Uses Leaflet + leaflet.heat (no API key needed, OSM tiles).
 * Points come from /api/listings/heatmap which returns aggregated 0.1° grid
 * cells — no listing IDs, no supplier identity. Pure community-level visual.
 */
export default function MapHeatmap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [stats, setStats] = useState({ total: 0, clusters: 0, suppliers: 0, carbonKg: 0, carbonTonnes: 0 });

  // Hide the literal "X pieces across Y regions" stat-line until the network
  // has at least this many active suppliers. Keeps the visual but avoids
  // making the early-stage scale obvious.
  const SHOW_STATS_AT_SUPPLIERS = 5;
  const showStats = stats.suppliers >= SHOW_STATS_AT_SUPPLIERS;

  useEffect(() => {
    let map;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await axios.get(`${API}/listings/heatmap`);
        if (cancelled || !containerRef.current) return;

        const points = data.points || [];
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }

        // Centre on Australia by default; if no Aussie points fall back to
        // the world. We pick a sensible zoom-out so the user can see the
        // continent in one glance.
        map = L.map(containerRef.current, {
          zoomControl: true,
          scrollWheelZoom: false,
          dragging: true,
          attributionControl: true,
        }).setView([-25.0, 134.0], 4);
        mapRef.current = map;

        // Carto dark tiles WITH labels so users can identify cities/countries.
        // Free, no API key required.
        L.tileLayer(
          'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
          {
            attribution: '&copy; OSM &copy; CARTO',
            subdomains: 'abcd',
            maxZoom: 18,
          },
        ).addTo(map);

        // Sqrt scaling so a cluster of 1 still has noticeable intensity even
        // when another cluster has 100. Linear scaling produces a single hot
        // spot and invisible specks elsewhere.
        const maxCount = Math.max(1, ...points.map((p) => p.count));
        const heatPoints = points.map((p) => [
          p.lat,
          p.lng,
          Math.max(0.35, Math.sqrt(p.count / maxCount)),
        ]);

        L.heatLayer(heatPoints, {
          radius: 38,
          blur: 26,
          minOpacity: 0.55,
          maxZoom: 8,
          gradient: {
            0.0: 'rgba(201,169,97,0.35)',
            0.3: 'rgba(201,169,97,0.7)',
            0.6: '#c9a961',
            0.85: '#f97316',
            1.0: '#ef4444',
          },
        }).addTo(map);

        // Lightweight labels so users can see *which* regions are hot
        // without giving away identifying info. Markers are kept invisible —
        // we just bind tooltips at each cluster's centroid.
        points.forEach((p) => {
          const label = `${p.count} item${p.count === 1 ? '' : 's'}`;
          L.circleMarker([p.lat, p.lng], {
            radius: 4,
            color: '#c9a961',
            weight: 1,
            fillColor: '#c9a961',
            fillOpacity: 0.6,
          })
            .bindTooltip(label, { permanent: false, direction: 'top', offset: [0, -6] })
            .addTo(map);
        });

        setStats({
          total: data.total_listings || 0,
          clusters: points.length,
          suppliers: data.supplier_count || 0,
          carbonKg: data.carbon_saved_kg || 0,
          carbonTonnes: data.carbon_saved_tonnes || 0,
        });
      } catch (e) {
        devError('heatmap load failed', e);
      }
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return (
    <section className="relative" data-testid="map-heatmap-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Where the stock is</p>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-2">
          Live supply heatmap
        </h2>
        <p className="text-sm text-muted-foreground max-w-2xl">
          {showStats ? (
            <>
              <strong className="text-foreground">{stats.total}</strong> available pieces across{' '}
              <strong className="text-foreground">{stats.clusters}</strong> regions worldwide. Brighter
              areas = more reclaimed material ready to ship.
            </>
          ) : (
            <>
              An indicative view of where reclaimed material is currently flowing.
              Brighter areas = active salvage activity.{' '}
              <span className="text-muted-foreground/60">More yards joining means more glow on the map.</span>
            </>
          )}
        </p>
        {stats.carbonKg > 0 && (
          <div
            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs"
            data-testid="map-heatmap-carbon-stat"
            title="Sum of estimated kg CO₂e saved (vs. buying new) across all available listings — Bath ICE v3.0 factors"
          >
            <Leaf className="w-3.5 h-3.5" />
            <strong className="text-emerald-200">
              {stats.carbonTonnes >= 1
                ? `${stats.carbonTonnes.toFixed(1)} tonnes`
                : `${Math.round(stats.carbonKg)} kg`}
            </strong> CO₂e saved across the marketplace
          </div>
        )}
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={containerRef}
          data-testid="map-heatmap-canvas"
          className="rounded-xl overflow-hidden border border-border/30 shadow-2xl"
          style={{ height: 480, width: '100%', background: '#0a0a0a' }}
        />
      </div>
    </section>
  );
}
